import React, { useState, useEffect, useMemo } from 'react';
import {
  Modal,
  Card,
  Row,
  Col,
  Typography,
  Tag,
  Table,
  Image,
  Timeline,
  Button,
  Space,
  Descriptions,
  Empty,
  Form,
  Skeleton,
  Input,
  InputNumber,
  message,
  Popconfirm,
  Spin,
  Statistic,
  Alert,
} from 'antd';
import { InfiniteSelect } from '../../Component/InfiniteSelect';
import {
  UserOutlined,
  CalendarOutlined,
  EnvironmentOutlined,
  PhoneOutlined,
  FileTextOutlined,
  DownloadOutlined,
  CloseOutlined,
  CheckOutlined,
  StopOutlined,
  CarOutlined,
  EditOutlined,
  PlusOutlined,
  DeleteOutlined,
  ExclamationCircleOutlined,
  DollarOutlined,
  GiftOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { DetailedOrder, OrderHistory } from '../../types/types';
import Api from '../../Network';
import CustomerClassificationModal from '../Customers/CustomerClassificationModal';
import NameByLang from '../../Component/nameByLang';
import { usePermissions } from '../../contexts/PermissionContext';
import './OrderDetailsModal.scss';
import './OrderResponsiveEnhancements.scss';

const { Text } = Typography;

interface OrderDetailsModalProps {
  visible: boolean;
  onClose: () => void;
  orderId: number | null;
}

const OrderDetailsModal: React.FC<OrderDetailsModalProps> = ({ visible, onClose, orderId }) => {
  const { t } = useTranslation();
  const { hasPermission } = usePermissions();
  const [order, setOrder] = useState<DetailedOrder | null>(null);
  const [loading, setLoading] = useState(false);

  // New states for order actions
  const [assignDriverModalVisible, setAssignDriverModalVisible] = useState(false);
  const [invoiceReferenceModalVisible, setInvoiceReferenceModalVisible] = useState(false);
  const [declineReasonModalVisible, setDeclineReasonModalVisible] = useState(false);
  const [classificationModalVisible, setClassificationModalVisible] = useState(false);
  const [, setSelectedDriverId] = useState<number | null>(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState<any>(null);

  // Order editing states
  const [isEditingOrder, setIsEditingOrder] = useState(false);
  const [editedOrderItems, setEditedOrderItems] = useState<any[]>([]);
  const [orderEditLoading, setOrderEditLoading] = useState(false);

  // New states for adding/removing items
  const [newItemKey, setNewItemKey] = useState(0);
  const [, setDeletedItems] = useState<number[]>([]);

  const [assignDriverForm] = Form.useForm();
  const [invoiceForm] = Form.useForm();
  const [declineReasonForm] = Form.useForm();

  const translateOrderText = (value?: string) => {
    if (!value) return value;
    const normalized = value.trim().toLowerCase();

    if (normalized === 'accepted offer and created order') {
      return t(
        'orders.historyDescriptions.acceptedOfferAndCreatedOrder',
        'Offer accepted and order created'
      );
    }

    if (normalized === 'offer accepted') {
      return t('orders.notesValues.offerAccepted', 'Offer accepted');
    }

    return value;
  };

  // Fetch order details with better error handling
  const fetchOrderDetails = async (id: number) => {
    setLoading(true);
    try {
      const response: any = await Api.get(`/api/admin/orders/${id}`);
      if (response.status && response.data) {
        setOrder(response.data);
      } else {
        // Error fetching order details
      }
    } catch (error) {
      // Error fetching order details
    } finally {
      setLoading(false);
    }
  };

  // Optimized useEffect with better loading states
  useEffect(() => {
    if (visible && orderId) {
      // Only fetch if we don't have the order data or if it's a different order
      if (!order || order.id !== orderId) {
        fetchOrderDetails(orderId);
      }
    } else if (!visible) {
      // Clear data when modal closes to prevent showing stale data on next open
      setOrder(null);
      setIsEditingOrder(false);
      setEditedOrderItems([]);
    }
  }, [visible, orderId, order?.id]);

  // Order editing functions
  const handleStartEditOrder = () => {
    setIsEditingOrder(true);
    // Initialize edited items with current order items
    setEditedOrderItems(order?.order_items?.map((item) => ({ ...item })) || []);
    setDeletedItems([]);
    setNewItemKey(0);
  };

  const handleCancelEditOrder = () => {
    setIsEditingOrder(false);
    setEditedOrderItems([]);
    setDeletedItems([]);
    setNewItemKey(0);
  };

  const handleUpdateOrderItem = (itemId: number, field: string, value: any) => {
    setEditedOrderItems((prevItems) =>
      prevItems.map((item) => {
        if (item.id === itemId) {
          const updatedItem = { ...item, [field]: value };
          // Recalculate total when quantity changes (using original unit_price)
          if (field === 'quantity') {
            const quantity = parseFloat(String(value || 1));
            const unitPrice = parseFloat(String(updatedItem.unit_price || 0));
            updatedItem.total = quantity * unitPrice;
          }
          return updatedItem;
        }
        return item;
      })
    );
  };

  // New function to add a new item
  const handleAddNewItem = () => {
    const newItem = {
      id: `new-${newItemKey}`,
      key: `new-${newItemKey}`,
      product_id: null,
      product: null,
      quantity: 1,
      unit_price: '0.00',
      tax_percent: '15',
      total: 0,
      isNew: true,
    };
    setEditedOrderItems((prevItems) => [...prevItems, newItem]);
    setNewItemKey((prev) => prev + 1);
  };

  // New function to delete an item (set quantity to 0)
  const handleDeleteItem = (itemId: number | string) => {
    if (typeof itemId === 'string' && itemId.startsWith('new-')) {
      // Remove new item that hasn't been saved yet
      setEditedOrderItems((prevItems) => prevItems.filter((item) => item.id !== itemId));
    } else {
      // Set quantity to 0 for existing items (backend will delete them)
      setEditedOrderItems((prevItems) =>
        prevItems.map((item) => {
          if (item.id === itemId) {
            return { ...item, quantity: 0, total: 0 };
          }
          return item;
        })
      );
    }
  };

  // New function to update new item
  const handleUpdateNewItem = (itemId: string, field: string, value: any) => {
    setEditedOrderItems((prevItems) =>
      prevItems.map((item) => {
        if (item.id === itemId) {
          const updatedItem = { ...item, [field]: value };
          // Recalculate total when quantity or unit_price changes
          if (field === 'quantity' || field === 'unit_price') {
            const quantity =
              field === 'quantity'
                ? parseFloat(String(value || 1))
                : parseFloat(String(updatedItem.quantity || 1));
            const unitPrice =
              field === 'unit_price'
                ? parseFloat(String(value || 0))
                : parseFloat(String(updatedItem.unit_price || 0));
            updatedItem.total = quantity * unitPrice;
          }
          return updatedItem;
        }
        return item;
      })
    );
  };

  // New function to handle product selection for new items
  const handleProductSelectForNewItem = (itemId: string, productId: number, product: any) => {
    setEditedOrderItems((prevItems) =>
      prevItems.map((item) => {
        if (item.id === itemId) {
          const unitPrice = parseFloat(String(product.selling_price || 0));
          const quantity = parseFloat(String(item.quantity || 1));
          const updatedItem = {
            ...item,
            product_id: productId,
            product: product,
            unit_price: unitPrice.toString(),
            total: quantity * unitPrice,
          };
          return updatedItem;
        }
        return item;
      })
    );
  };

  const handleSaveOrderChanges = async () => {
    if (!order) return;

    setOrderEditLoading(true);
    try {
      // Prepare data for API (matching the curl structure)
      // Include all items including those with quantity 0 (for deletion)
      const updateData = {
        line_items: editedOrderItems.map((item) => ({
          product_id: item.product_id || item.product?.id,
          quantity: parseFloat(String(item.quantity || 0)),
          unit_price: parseFloat(String(item.unit_price || 0)).toString(),
        })),
      };

      const response: any = await Api.put(`/api/admin/orders/${order.id}`, updateData);

      // Check for successful response based on actual API structure
      if (response && response.status === true && response.code === 200) {
        message.success(t('orders.messages.orderUpdated'));
        setIsEditingOrder(false);
        setEditedOrderItems([]);
        setDeletedItems([]);
        setNewItemKey(0);
        // Refresh order details
        fetchOrderDetails(order.id);
      } else {
        message.error(t('orders.messages.orderUpdateFailed'));
      }
    } catch (error: any) {
      message.error(t('orders.messages.orderUpdateFailed'));
    } finally {
      setOrderEditLoading(false);
    }
  };

  // Assign driver to order
  const assignDriverToOrder = async (driverId: number) => {
    if (!order) return;

    setActionLoading(true);
    try {
      const response: any = await Api.post(`/api/admin/orders/${order.id}/assign-driver`, {
        driver_id: driverId,
      });

      if (response.status) {
        message.success(t('orders.messages.driverAssigned'));
        setAssignDriverModalVisible(false);
        assignDriverForm.resetFields();
        // Refresh order details
        fetchOrderDetails(order.id);
      }
    } catch (error) {
      // Error assigning driver
    } finally {
      setActionLoading(false);
    }
  };

  // Update order status (approve/decline)
  const updateOrderStatus = async (newStatus: 'Approved' | 'Declined', declineReason?: string) => {
    if (!order) return;

    setActionLoading(true);
    try {
      const payload: any = { status: newStatus };
      if (declineReason) {
        payload.decline_reason = declineReason;
      }

      const response: any = await Api.post(
        `/api/admin/orders/status/${order.id}/${newStatus}`,
        payload
      );

      if (response.status) {
        const messageKey = newStatus === 'Approved' ? 'orderApproved' : 'orderRejected';
        message.success(t(`orders.messages.${messageKey}`));
        // Refresh order details
        fetchOrderDetails(order.id);
      }
    } catch (error) {
      // Error updating order status
    } finally {
      setActionLoading(false);
    }
  };

  // Update invoice reference
  const updateInvoiceReference = async (invoiceRef: string) => {
    if (!order) return;

    setActionLoading(true);
    try {
      const response: any = await Api.put(`/api/admin/orders/${order.id}`, {
        invoice_reference: invoiceRef,
      });

      if (response.status) {
        message.success(t('orders.messages.orderUpdated'));
        setInvoiceReferenceModalVisible(false);
        invoiceForm.resetFields();
        // Refresh order details
        fetchOrderDetails(order.id);
      }
    } catch (error) {
      // Error updating invoice reference
    } finally {
      setActionLoading(false);
    }
  };

  // Memoized helper functions to determine which actions to show (avoid recalculation on every render)
  const shouldShowAssignDriver = useMemo(() => {
    return order?.status === 'Approved' && order?.shipping_status !== 'Delivered';
  }, [order?.status, order?.shipping_status]);

  const shouldShowAcceptDecline = useMemo(() => {
    return (
      (order?.status === 'Draft' || order?.status === null || order?.status === undefined) &&
      (order?.shipping_status === null || order?.shipping_status === undefined)
    );
  }, [order?.status, order?.shipping_status]);

  const shouldShowInvoiceReference = useMemo(() => {
    return order?.status === 'Approved'; // Show for approved orders
  }, [order?.status]);

  const shouldShowCancel = useMemo(() => {
    return (
      order?.status !== 'Cancelled' &&
      order?.status !== 'Declined' &&
      order?.status !== 'Completed' &&
      order?.shipping_status !== 'Delivered'
    );
  }, [order?.status, order?.shipping_status]);

  // Modal handlers
  const handleAssignDriverModalOpen = () => {
    setAssignDriverModalVisible(true);
    assignDriverForm.resetFields();
  };

  const handleAssignDriverModalClose = () => {
    setAssignDriverModalVisible(false);
    assignDriverForm.resetFields();
    setSelectedDriverId(null);
  };

  const handleInvoiceReferenceModalOpen = () => {
    setInvoiceReferenceModalVisible(true);
    invoiceForm.setFieldsValue({ invoice_reference: order?.invoice_reference || '' });
  };

  const handleInvoiceReferenceModalClose = () => {
    setInvoiceReferenceModalVisible(false);
    invoiceForm.resetFields();
  };

  const handleDeclineReasonModalClose = () => {
    setDeclineReasonModalVisible(false);
    declineReasonForm.resetFields();
  };

  // Form submission handlers
  const handleAssignDriverSubmit = (values: any) => {
    assignDriverToOrder(values.driver_id);
  };

  const handleInvoiceReferenceSubmit = (values: any) => {
    updateInvoiceReference(values.invoice_reference);
  };

  const handleDeclineReasonSubmit = (values: any) => {
    updateOrderStatus('Declined', values.decline_reason);
    setDeclineReasonModalVisible(false);
    declineReasonForm.resetFields();
  };

  // Check customer classification before approving order
  const checkCustomerClassification = async (customerId: number) => {
    try {
      const response: any = await Api.get(`/api/admin/customers/${customerId}`);
      const customer = response?.data?.customer || response?.data?.data || response?.data;
      return (
        customer?.customer_classification_id !== null &&
        customer?.customer_classification_id !== undefined
      );
    } catch (error) {
      return false;
    }
  };

  const handleApproveOrder = async () => {
    if (!order || !order.customer?.id) return;

    // Check if customer has classification
    const hasClassification = await checkCustomerClassification(order.customer.id);
    if (!hasClassification) {
      // Fetch customer data for classification modal
      try {
        const response: any = await Api.get(`/api/admin/customers/${order.customer.id}`);
        const customer = response?.data?.customer || response?.data?.data || response?.data;
        setSelectedCustomer(customer);
        setClassificationModalVisible(true);
        message.warning(
          t(
            'orders.validation.classifyCustomerFirst',
            'Please classify the customer before approving the order'
          )
        );
        return;
      } catch (error) {
        message.error(t('orders.validation.customerNotFound', 'Customer not found'));
        return;
      }
    }

    // Proceed with order approval
    updateOrderStatus('Approved');
  };

  // Handle classification modal success
  const handleClassificationSuccess = async () => {
    setClassificationModalVisible(false);
    if (order && order.customer?.id) {
      // Refresh customer data and approve order
      try {
        const response: any = await Api.get(`/api/admin/customers/${order.customer.id}`);
        const customer = response?.data?.customer || response?.data?.data || response?.data;
        setSelectedCustomer(customer);
        // Now approve the order
        updateOrderStatus('Approved');
      } catch (error) {
        message.error(t('orders.messages.operationFailed', 'Operation failed'));
      }
    }
  };

  const handleDeclineOrder = () => {
    setDeclineReasonModalVisible(true);
    declineReasonForm.resetFields();
  };

  const handleCancelOrder = async () => {
    if (!order) return;

    setActionLoading(true);
    try {
      const response: any = await Api.get(`/api/admin/orders/${order.id}/Canceled`);

      if (response.status) {
        message.success(t('orders.messages.orderCancelled', 'Order cancelled successfully'));
        // Refresh order details
        fetchOrderDetails(order.id);
      } else {
        message.error(
          t('orders.messages.orderCancelFailed', 'Failed to cancel order. Please try again.')
        );
      }
    } catch (error: any) {
      message.error(
        t('orders.messages.orderCancelFailed', 'Failed to cancel order. Please try again.')
      );
    } finally {
      setActionLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    const statusColors: { [key: string]: string } = {
      new: 'blue',
      approved: 'green',
      current: 'orange',
      completed: 'success',
      declined: 'red',
      cancelled: 'red',
      pending: 'default',
      draft: 'default',
    };
    return statusColors[status.toLowerCase()] || 'default';
  };

  const calculateOrderTotals = (orderItems: any[], orderRef?: DetailedOrder | null) => {
    const offerPriceRaw =
      orderRef?.is_offer && orderRef.offer?.special_price != null
        ? orderRef.offer.special_price
        : null;

    if (offerPriceRaw !== null && String(offerPriceRaw) !== '') {
      const subtotal = parseFloat(String(offerPriceRaw));
      if (!Number.isNaN(subtotal)) {
        const taxAmount = subtotal * (15 / 100);

        const loyaltyDiscount = parseFloat(String((orderRef ?? order)?.loyalty_discount || 0));

        const totalBeforeDiscount = subtotal + taxAmount;
        const total = totalBeforeDiscount - loyaltyDiscount;

        return { subtotal, taxAmount, loyaltyDiscount, totalBeforeDiscount, total };
      }
    }

    // Filter out items with quantity 0 for calculations
    const activeItems = orderItems.filter((item) => (item.quantity || 0) > 0);

    const subtotal = activeItems.reduce((sum, item) => {
      const total = item.total || 0;
      return sum + total;
    }, 0);

    const taxAmount = subtotal * (15 / 100);

    // Get loyalty discount from order (if exists)
    const loyaltyDiscount = parseFloat(String((orderRef ?? order)?.loyalty_discount || 0));

    // Total before discount
    const totalBeforeDiscount = subtotal + taxAmount;
    // Final total after discount
    const total = totalBeforeDiscount - loyaltyDiscount;

    return { subtotal, taxAmount, loyaltyDiscount, totalBeforeDiscount, total };
  };

  const renderStatusProgression = () => {
    const statusSteps = [
      {
        key: 'approved',
        title: t('orders.statusProgression.approved.title'),
        // description: t('orders.statusProgression.approved.description'),
      },
      {
        key: 'assigned_to_driver',
        title: t('orders.statusProgression.assignedToDriver.title'),
        // description: t('orders.statusProgression.assignedToDriver.description'),
      },
      {
        key: 'preparing',
        title: t('orders.statusProgression.preparing.title'),
        // description: t('orders.statusProgression.preparing.description'),
      },
      {
        key: 'in_delivery',
        title: t('orders.statusProgression.inDelivery.title'),
        // description: t('orders.statusProgression.inDelivery.description'),
      },
      {
        key: 'delivered',
        title: t('orders.statusProgression.delivered.title'),
        // description: t('orders.statusProgression.delivered.description'),
      },
    ];

    // Determine current step based on order.status and order.shipping_status (delivery status)
    let currentStep = 0;

    const orderStatus = order?.status;
    const deliveryStatus = order?.shipping_status; // This represents delivery status
    const driverId = order?.driver_id;

    // Logic based ONLY on order.status AND order.shipping_status
    if (orderStatus === 'Declined') {
      currentStep = -1;
    } else if (orderStatus === 'Approved') {
      // Approved orders - determine step based ONLY on shipping_status
      if (deliveryStatus === 'Delivered') {
        currentStep = 5; // All steps completed - تم التسليم بالكامل
      } else if (deliveryStatus === 'Delivery') {
        currentStep = 4; // جاري التوصيل
      } else if (deliveryStatus === 'Processing') {
        currentStep = 3; //   تم تسليم الطلب للمندوب
      } else if (driverId !== null) {
        currentStep = 2; // جاري تجهيز الطلبية
      } else {
        // Approved with no specific shipping status = just assigned to driver
        currentStep = 1; // تمت الموافقة
      }
    } else if (orderStatus === 'New' || orderStatus === 'Pending') {
      currentStep = 0; // انتظار الموافقة
    } else {
      currentStep = 0; // Default: انتظار الموافقة
    }

    if (order?.status === 'Declined') {
      return (
        <div className="declined-status">
          <div className="declined-content">
            <div className="decline-icon">❌</div>
            <div className="decline-title">{t('orders.status.declined')}</div>
            {order.decline_reason && (
              <div className="decline-reason">
                <strong>{t('orders.orderDetails.declineReason')}:</strong> {order.decline_reason}
              </div>
            )}
          </div>
        </div>
      );
    }

    return (
      <div className="status-progression">
        <div className="progression-container">
          {statusSteps.map((step, index) => {
            const isCompleted = index < currentStep;
            const isCurrent = index === currentStep;

            return (
              <div
                key={step.key}
                className={`step-container ${isCompleted ? 'completed' : ''} ${isCurrent ? 'current' : ''}`}
              >
                {/* Step circle */}
                <div
                  className={`step-circle ${isCompleted ? 'completed' : ''} ${isCurrent ? 'current' : ''}`}
                >
                  {isCompleted ? '✓' : index + 1}
                </div>

                {/* Step content */}
                <div className="step-content">
                  <div className="step-title">{step.title}</div>
                </div>

                {/* Connection line */}
                {index < statusSteps.length - 1 && (
                  <div className={`connection-line ${isCompleted ? 'completed' : ''}`} />
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // Memoized calculations to avoid recalculating on every render
  const currentOrderItems = useMemo(() => {
    return isEditingOrder ? editedOrderItems : order?.order_items || [];
  }, [isEditingOrder, editedOrderItems, order?.order_items]);

  // Helper function to analyze changes between old and new items for a history event
  const analyzeItemChanges = (historyEvent: any) => {
    if (!historyEvent || historyEvent.event !== 'updated_items') return null;

    const fromItems = (historyEvent.from?.orderItems || []).filter((item: unknown) => item != null);
    const toItems = (historyEvent.to?.orderItems || []).filter((item: unknown) => item != null);

    const changes = {
      deleted: [] as any[],
      modified: [] as any[],
      added: [] as any[],
      unchanged: [] as any[],
    };

    // Create maps for easier lookup
    const fromMap = new Map(fromItems.map((item: any) => [item.id, item]));
    const toMap = new Map(toItems.map((item: any) => [item.id, item]));

    // Find deleted items (in from but not in to)
    fromItems.forEach((item: any) => {
      if (!toMap.has(item.id)) {
        changes.deleted.push(item);
      }
    });

    // Find added items (in to but not in from)
    toItems.forEach((item: any) => {
      if (!fromMap.has(item.id)) {
        changes.added.push(item);
      }
    });

    // Find modified items (same ID but different quantity or price)
    toItems.forEach((toItem: any) => {
      const fromItem = fromMap.get(toItem.id);
      if (fromItem) {
        const qtyChanged =
          parseFloat(String((fromItem as any).quantity || 0)) !==
          parseFloat(String((toItem as any).quantity || 0));
        const priceChanged =
          parseFloat(String((fromItem as any).unit_price || 0)) !==
          parseFloat(String((toItem as any).unit_price || 0));

        if (qtyChanged || priceChanged) {
          changes.modified.push({
            from: fromItem,
            to: toItem,
          });
        } else {
          changes.unchanged.push(toItem);
        }
      }
    });

    return changes;
  };

  const orderTotals = useMemo(() => {
    return calculateOrderTotals(currentOrderItems, order);
  }, [currentOrderItems, order]);

  const { subtotal, loyaltyDiscount } = orderTotals;

  // Calculate subtotal after discount (if discount exists)
  const subtotalAfterDiscount = loyaltyDiscount > 0 ? subtotal - loyaltyDiscount : null;

  // Calculate tax amount: if there's a discount, calculate tax on subtotalAfterDiscount, otherwise on subtotal
  const baseAmountForTax = subtotalAfterDiscount !== null ? subtotalAfterDiscount : subtotal;
  const taxAmount = baseAmountForTax * (15 / 100);
  const totalBeforeDiscount = baseAmountForTax + taxAmount;

  // Memoize table columns to prevent unnecessary re-renders
  const orderItemsColumns = useMemo(
    () => [
      {
        title: t('orders.orderDetails.product'),
        dataIndex: ['product', 'name_ar'],
        key: 'product',
        'data-label': t('orders.orderDetails.product'),
        render: (name: string, record: any) => {
          if (isEditingOrder && record.isNew) {
            // For new items, show product selector
            return (
              <div data-label={t('orders.orderDetails.product')}>
                <InfiniteSelect
                  queryKey="api/admin/products"
                  placeholder={t('orders.placeholders.selectProduct')}
                  showSearch
                  singleMode={true}
                  valueField="id"
                  searchField="name_ar,name_en"
                  style={{ width: '100%' }}
                  initialOptions={record.product ? [record.product] : []}
                  onChange={(productId: number, option: any) => {
                    const product = option?.item || option;
                    if (product && productId) {
                      handleProductSelectForNewItem(record.id, productId, product);
                    }
                  }}
                  labelRender={(option: any) => {
                    const product = option?.item || option;
                    if (!product) {
                      return t('common.noData');
                    }
                    return `${product.name_ar || product.name_en || ''} - ${product.selling_price || 0} ${t('common.SAR')}`;
                  }}
                />
              </div>
            );
          }
          return (
            <div className="product-info" data-label={t('orders.orderDetails.product')}>
              <div className="product-name">{name || record.product?.name_ar}</div>
              <Text type="secondary" className="product-name-en">
                {record.product?.name_en}
              </Text>
            </div>
          );
        },
      },
      {
        title: t('orders.orderDetails.quantity'),
        dataIndex: 'quantity',
        key: 'quantity',
        width: 100,
        'data-label': t('orders.orderDetails.quantity'),
        render: (quantity: string | number | undefined, record: any) => {
          if (isEditingOrder) {
            const editedItem = editedOrderItems.find((item) => item.id === record.id);
            const currentQuantity =
              editedItem?.quantity !== undefined
                ? editedItem.quantity
                : parseFloat(String(quantity || 1));

            return (
              <div data-label={t('orders.orderDetails.quantity')}>
                <InputNumber
                  min={0}
                  step={0.01}
                  value={currentQuantity}
                  onChange={(value) => {
                    if (record.isNew) {
                      handleUpdateNewItem(record.id, 'quantity', value || 0);
                    } else {
                      handleUpdateOrderItem(record.id, 'quantity', value || 0);
                    }
                  }}
                  className="quantity-input"
                />
              </div>
            );
          }
          const quantityValue = typeof quantity === 'string' ? parseFloat(quantity) : quantity || 1;
          return (
            <div data-label={t('orders.orderDetails.quantity')}>
              <Text strong className="quantity-text">
                {quantityValue.toFixed(2)}
              </Text>
            </div>
          );
        },
      },
      {
        title: t('orders.orderDetails.unitPrice'),
        dataIndex: 'unit_price',
        key: 'unit_price',
        width: 120,
        'data-label': t('orders.orderDetails.unitPrice'),
        render: (price: string | number | undefined, record: any) => {
          if (isEditingOrder && record.isNew) {
            return (
              <div data-label={t('orders.orderDetails.unitPrice')}>
                <InputNumber
                  min={0}
                  step={0.01}
                  value={parseFloat(String(record.unit_price || 0))}
                  onChange={(value) => handleUpdateNewItem(record.id, 'unit_price', value || 0)}
                  formatter={(value) => `${value} ${t('common.SAR')}`}
                  parser={(value) => parseFloat(value!.replace(t('common.SAR'), ''))}
                  className="price-input"
                />
              </div>
            );
          }
          const priceValue = typeof price === 'string' ? parseFloat(price) : price || 0;
          return (
            <div data-label={t('orders.orderDetails.unitPrice')}>
              <Text className="price-text">
                {priceValue.toFixed(2)} {t('common.SAR')}
              </Text>
            </div>
          );
        },
      },
      {
        title: t('orders.orderDetails.subtotal'),
        key: 'subtotal',
        width: 120,
        'data-label': t('orders.orderDetails.subtotal'),
        render: (record: any) => {
          const quantity = parseFloat(String(record.quantity || 1));
          const unitPrice = parseFloat(String(record.unit_price || 0));
          const subtotal = quantity * unitPrice;
          return (
            <div data-label={t('orders.orderDetails.subtotal')}>
              <Text className="subtotal-text">
                {subtotal.toFixed(2)} {t('common.SAR')}
              </Text>
            </div>
          );
        },
      },
      {
        title: t('orders.orderDetails.tax'),
        dataIndex: 'tax_percent',
        key: 'tax_percent',
        width: 80,
        'data-label': t('orders.orderDetails.tax'),
        render: (tax: string | number | undefined) => {
          const taxValue = typeof tax === 'string' ? tax : String(tax || 15);
          return (
            <div data-label={t('orders.orderDetails.tax')}>
              <Text className="tax-percent-text">{taxValue}%</Text>
            </div>
          );
        },
      },
      {
        title: t('orders.orderDetails.taxAmount'),
        key: 'tax_amount',
        width: 120,
        'data-label': t('orders.orderDetails.taxAmount'),
        render: (record: any) => {
          const quantity = parseFloat(String(record.quantity || 1));
          const unitPrice = parseFloat(String(record.unit_price || 0));
          const taxPercent = parseFloat(String(record.tax_percent || 15));
          const subtotal = quantity * unitPrice;
          const taxAmount = subtotal * (taxPercent / 100);
          return (
            <div data-label={t('orders.orderDetails.taxAmount')}>
              <Text className="tax-amount-text">
                {taxAmount.toFixed(2)} {t('common.SAR')}
              </Text>
            </div>
          );
        },
      },
      {
        title: t('orders.orderDetails.totalWithTax'),
        key: 'total_with_tax',
        width: 140,
        'data-label': t('orders.orderDetails.totalWithTax'),
        render: (record: any) => {
          const quantity = parseFloat(String(record.quantity || 1));
          const unitPrice = parseFloat(String(record.unit_price || 0));
          const taxPercent = parseFloat(String(record.tax_percent || 15));
          const subtotal = quantity * unitPrice;
          const taxAmount = subtotal * (taxPercent / 100);
          const totalWithTax = subtotal + taxAmount;
          return (
            <div data-label={t('orders.orderDetails.totalWithTax')}>
              <Text strong className="total-with-tax-text">
                {totalWithTax.toFixed(2)} {t('common.SAR')}
              </Text>
            </div>
          );
        },
      },
      // Actions column for editing mode
      ...(isEditingOrder
        ? [
            {
              title: t('common.actions'),
              key: 'actions',
              width: 80,
              'data-label': t('common.actions'),
              render: (_: unknown, record: any) => {
                const editedItem = editedOrderItems.find((item) => item.id === record.id);
                const currentQuantity =
                  editedItem?.quantity !== undefined
                    ? editedItem.quantity
                    : parseFloat(String(record.quantity || 1));

                // If quantity is 0, show "Deleted" status
                if (currentQuantity === 0) {
                  return (
                    <div data-label={t('common.actions')}>
                      <Tag color="red" style={{ fontSize: '12px' }}>
                        {t('orders.status.deleted', 'Deleted')}
                      </Tag>
                    </div>
                  );
                }

                return (
                  <div data-label={t('common.actions')}>
                    <Popconfirm
                      title={t(
                        'orders.confirmDeleteItem',
                        'Are you sure you want to remove this item?'
                      )}
                      onConfirm={() => handleDeleteItem(record.id)}
                      okText={t('common.yes')}
                      cancelText={t('common.no')}
                      okType="danger"
                    >
                      <Button
                        type="text"
                        danger
                        icon={<DeleteOutlined />}
                        size="small"
                        title={t('common.delete')}
                      />
                    </Popconfirm>
                  </div>
                );
              },
            },
          ]
        : []),
    ],
    [isEditingOrder, editedOrderItems, t]
  );

  if (!order) {
    return (
      <Modal
        title={t('orders.orderDetails.title')}
        open={visible}
        onCancel={onClose}
        footer={null}
        width={1200}
        className="order-details-modal"
      >
        {loading ? (
          <div style={{ padding: '24px' }}>
            {/* Skeleton loading for better UX */}
            <div style={{ marginBottom: '24px' }}>
              <Skeleton active paragraph={{ rows: 1 }} />
            </div>
            <div style={{ marginBottom: '24px' }}>
              <Skeleton active paragraph={{ rows: 3 }} />
            </div>
            <div style={{ marginBottom: '24px' }}>
              <Skeleton active paragraph={{ rows: 4 }} />
            </div>
            <div style={{ marginBottom: '24px' }}>
              <Skeleton active paragraph={{ rows: 2 }} />
            </div>
          </div>
        ) : (
          <div style={{ padding: '48px 24px', textAlign: 'center' }}>
            <Empty description={t('orders.messages.noOrderFound')} />
          </div>
        )}
      </Modal>
    );
  }

  return (
    <Modal
      title={
        <div className="modal-title">
          <FileTextOutlined />
          <span className="title-text">
            {loading ? (
              <span>
                {t('common.loading')} <Spin size="small" style={{ marginLeft: 8 }} />
              </span>
            ) : (
              `${t('orders.orderDetails.title')} - ${order.reference}`
            )}
          </span>
        </div>
      }
      open={visible}
      onCancel={onClose}
      footer={
        <div className="modal-footer">
          <Button icon={<CloseOutlined />} onClick={onClose}>
            {t('common.close')}
          </Button>
        </div>
      }
      width="100vw"
      style={{
        top: 0,
        maxWidth: '100vw',
        margin: 0,
        padding: 0,
        height: '100vh',
      }}
      bodyStyle={{
        height: 'auto',
        minHeight: 'calc(100vh - 120px)',
        maxHeight: 'calc(100vh - 120px)',
        overflowY: 'auto',
        padding: '24px',
        margin: 0,
      }}
      className="order-details-modal order-modal-enhanced fullscreen-modal"
      centered={false}
      destroyOnClose
    >
      {loading ? (
        // Show skeleton loading immediately while data loads
        <div style={{ padding: '0 24px' }}>
          <Skeleton active paragraph={{ rows: 4 }} />
          <Skeleton active paragraph={{ rows: 6 }} style={{ marginTop: 24 }} />
          <Skeleton active paragraph={{ rows: 4 }} style={{ marginTop: 24 }} />
        </div>
      ) : order ? (
        <div style={{ width: '100%' }}>
          {/* Order Header */}
          <Card
            className={`order-header-card order-card-enhanced ${order.is_offer ? 'order-header-card--offer' : ''}`}
          >
            <Row gutter={[24, 16]}>
              <Col xs={24} sm={12}>
                <Descriptions column={1} size="small" className="order-info">
                  <Descriptions.Item label={t('orders.columns.reference')}>
                    <Space size={8} wrap>
                      <Text code strong className="reference-text">
                        {order.reference}
                      </Text>
                      {order.is_offer && (
                        <Tag className="offer-order-badge">
                          <GiftOutlined className="offer-order-badge__icon" />
                          {t('orders.labels.offerOrder', 'Offer Order')}
                        </Tag>
                      )}
                    </Space>
                  </Descriptions.Item>
                  <Descriptions.Item label={t('orders.columns.status')}>
                    {order.status === 'Approved' && order.shipping_status === 'Delivered' ? (
                      <Tag
                        color="green"
                        className="status-tag delivered order-status-tag status-delivered"
                      >
                        ✅ {t('orders.status.delivered', 'تم التسليم')}
                      </Tag>
                    ) : (
                      <Tag
                        color={getStatusColor(order.status)}
                        className="status-tag order-status-tag status-${order.status.toLowerCase()}"
                      >
                        {t(`orders.status.${order.status.toLowerCase()}`, order.status)}
                      </Tag>
                    )}
                  </Descriptions.Item>
                  <Descriptions.Item label={t('orders.columns.location')}>
                    <div className="location-info">
                      <EnvironmentOutlined /> {order.location}
                    </div>
                  </Descriptions.Item>
                  <Descriptions.Item label={t('orders.columns.createdAt')}>
                    <div className="date-info">
                      <CalendarOutlined /> {new Date(order.created_at).toLocaleString()}
                    </div>
                  </Descriptions.Item>
                </Descriptions>
              </Col>
              <Col xs={24} sm={12}>
                <Descriptions column={1} size="small" className="customer-driver-info">
                  <Descriptions.Item label={t('orders.orderDetails.customer')}>
                    {order.customer && (
                      <div className="customer-details">
                        <div className="customer-name">
                          <UserOutlined /> {order.customer.name}
                        </div>
                        <div className="customer-phone">
                          <PhoneOutlined /> {order.customer.phone_number}
                        </div>
                        {order.customer.tax_number && (
                          <div className="customer-tax">Tax: {order.customer.tax_number}</div>
                        )}
                        {order.customer.customer_classification && (
                          <div className="customer-classification" style={{ marginTop: 12 }}>
                            <div style={{ fontWeight: 'bold', marginBottom: 4 }}>
                              {t('customers.columns.classification', 'Customer Classification')}:
                            </div>
                            <div
                              style={{
                                display: 'flex',
                                flexWrap: 'wrap',
                                gap: '8px',
                                alignItems: 'center',
                              }}
                            >
                              {order.customer?.customer_classification?.customer_type && (
                                <span>
                                  <Text type="secondary">
                                    {t('customers.columns.customerType', 'Customer Type')}:
                                  </Text>{' '}
                                  <NameByLang
                                    name_en={
                                      order.customer.customer_classification.customer_type?.name_en
                                    }
                                    name_ar={
                                      order.customer.customer_classification.customer_type?.name_ar
                                    }
                                  />
                                </span>
                              )}
                              {order.customer?.customer_classification?.customer_type &&
                                (order.customer.customer_classification?.payment_method ||
                                  order.customer.customer_classification?.sales_representative) && (
                                  <span style={{ margin: '0 4px' }}>|</span>
                                )}
                              {order.customer?.customer_classification?.payment_method && (
                                <span>
                                  <Text type="secondary">
                                    {t('customers.columns.paymentMethod', 'Payment Method')}:
                                  </Text>{' '}
                                  <NameByLang
                                    name_en={
                                      order.customer.customer_classification.payment_method?.name_en
                                    }
                                    name_ar={
                                      order.customer.customer_classification.payment_method?.name_ar
                                    }
                                  />
                                </span>
                              )}
                              {order.customer?.customer_classification?.payment_method &&
                                order.customer.customer_classification?.sales_representative && (
                                  <span style={{ margin: '0 4px' }}>|</span>
                                )}
                              {order.customer?.customer_classification?.sales_representative && (
                                <span>
                                  <Text type="secondary">
                                    {t(
                                      'customers.columns.salesRepresentative',
                                      'Sales Representative'
                                    )}
                                    :
                                  </Text>{' '}
                                  {
                                    order.customer.customer_classification.sales_representative
                                      ?.name
                                  }
                                </span>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </Descriptions.Item>
                  {order.driver && (
                    <Descriptions.Item label={t('orders.orderDetails.driver')}>
                      <div className="driver-details">
                        <div className="driver-name">{order.driver.name}</div>
                        <div className="driver-phone">{order.driver.phone_number}</div>
                      </div>
                    </Descriptions.Item>
                  )}
                </Descriptions>
              </Col>
            </Row>
          </Card>

          {/* Customer Financial Information */}
          {order.customer && (
            <Card
              title={t('orders.orderDetails.customerFinancialInfo')}
              className="order-card-enhanced"
              style={{ marginBottom: 24 }}
            >
              <Row gutter={[16, 16]}>
                <Col xs={24} sm={12} md={8} lg={8}>
                  <Card
                    className="summary-card"
                    style={{
                      background: '#ff9800',
                      border: 'none',
                      borderRadius: '8px',
                    }}
                    bodyStyle={{ padding: '20px' }}
                  >
                    <Statistic
                      title={
                        <span style={{ color: '#ffffff' }}>
                          {t('customers.summary.totalDue', 'Total Due')}
                        </span>
                      }
                      value={order.customer.total_out_standing || 0}
                      suffix={t('common.SAR')}
                      valueStyle={{ color: '#ffffff', fontSize: '24px', fontWeight: 'bold' }}
                      prefix={<DollarOutlined style={{ color: '#ffffff', fontSize: '24px' }} />}
                    />
                  </Card>
                </Col>
                <Col xs={24} sm={12} md={8} lg={8}>
                  <Card
                    className="summary-card"
                    style={{
                      background: '#9c27b0',
                      border: 'none',
                      borderRadius: '8px',
                    }}
                    bodyStyle={{ padding: '20px' }}
                  >
                    <Statistic
                      title={
                        <span style={{ color: '#ffffff' }}>
                          {t('customers.summary.invoiceCount', 'Invoice Count')}
                        </span>
                      }
                      value={order.customer.total_invoices_count || 0}
                      valueStyle={{ color: '#ffffff', fontSize: '24px', fontWeight: 'bold' }}
                      prefix={<FileTextOutlined style={{ color: '#ffffff', fontSize: '24px' }} />}
                    />
                  </Card>
                </Col>
                <Col xs={24} sm={12} md={8} lg={8}>
                  <Card
                    className="summary-card"
                    style={{
                      background: '#2196f3',
                      border: 'none',
                      borderRadius: '8px',
                    }}
                    bodyStyle={{ padding: '20px' }}
                  >
                    <Statistic
                      title={
                        <span style={{ color: '#ffffff' }}>
                          {t('customers.summary.totalInvoices', 'Total Invoices')}
                        </span>
                      }
                      value={order.customer.total_invoices_amount || 0}
                      suffix={t('common.SAR')}
                      valueStyle={{ color: '#ffffff', fontSize: '24px', fontWeight: 'bold' }}
                      prefix={<FileTextOutlined style={{ color: '#ffffff', fontSize: '24px' }} />}
                    />
                  </Card>
                </Col>
                <Col xs={24} sm={12} md={12} lg={12}>
                  <Card
                    className="summary-card"
                    style={{
                      background: '#f44336',
                      border: 'none',
                      borderRadius: '8px',
                    }}
                    bodyStyle={{ padding: '20px' }}
                  >
                    <Statistic
                      title={
                        <span style={{ color: '#ffffff' }}>
                          {t('customers.summary.overdue', 'Overdue')}
                        </span>
                      }
                      value={order.customer.overdue || 0}
                      suffix={t('common.SAR')}
                      valueStyle={{ color: '#ffffff', fontSize: '24px', fontWeight: 'bold' }}
                      prefix={
                        <ExclamationCircleOutlined style={{ color: '#ffffff', fontSize: '24px' }} />
                      }
                    />
                  </Card>
                </Col>
                <Col xs={24} sm={12} md={12} lg={12}>
                  <Card
                    className="summary-card"
                    style={{
                      background: '#00bcd4',
                      border: 'none',
                      borderRadius: '8px',
                    }}
                    bodyStyle={{ padding: '20px' }}
                  >
                    <Statistic
                      title={
                        <span style={{ color: '#ffffff' }}>
                          {t('customers.summary.closingBalance', 'Closing Balance')}
                        </span>
                      }
                      value={order.customer.closing_balance || 0}
                      suffix={t('common.SAR')}
                      valueStyle={{ color: '#ffffff', fontSize: '24px', fontWeight: 'bold' }}
                      prefix={<DollarOutlined style={{ color: '#ffffff', fontSize: '24px' }} />}
                    />
                  </Card>
                </Col>
              </Row>
              {/* High Risk Warning - Only show if overdue amount is significant (>= 1000 SAR) */}
              {order.customer.overdue && parseFloat(String(order.customer.overdue || 0)) >= 1000 ? (
                <Alert
                  message={t('orders.orderDetails.highRiskWarning')}
                  type="warning"
                  icon={<ExclamationCircleOutlined />}
                  showIcon
                  style={{ marginTop: 16 }}
                />
              ) : null}
            </Card>
          )}

          {/* Order Items */}
          <Card
            title={t('orders.orderDetails.orderItems')}
            className="order-items-card order-card-enhanced add-items-section-enhanced"
            style={{ width: '100%', maxWidth: '100%', margin: 0 }}
            extra={
              order?.status !== 'Completed' &&
              order?.status !== 'Declined' &&
              (hasPermission('update_order') ||
                hasPermission('orders.update') ||
                hasPermission('edit_order')) && (
                <Space className="edit-actions order-actions-enhanced">
                  {isEditingOrder ? (
                    <>
                      <Button
                        type="primary"
                        size="small"
                        loading={orderEditLoading}
                        onClick={handleSaveOrderChanges}
                        className="ant-btn-success"
                        style={{ minWidth: '80px' }}
                      >
                        {t('common.save')}
                      </Button>
                      <Button
                        size="small"
                        onClick={handleCancelEditOrder}
                        style={{ minWidth: '80px' }}
                      >
                        {t('common.cancel')}
                      </Button>
                      <Button
                        type="dashed"
                        size="small"
                        icon={<PlusOutlined />}
                        onClick={handleAddNewItem}
                        className="add-item-button"
                        style={{ minWidth: '100px' }}
                      >
                        {t('orders.createOrder.addItem')}
                      </Button>
                    </>
                  ) : (
                    <Button
                      type="primary"
                      size="small"
                      icon={<EditOutlined />}
                      onClick={handleStartEditOrder}
                      style={{ minWidth: '120px' }}
                    >
                      {t('orders.orderDetails.editOrder')}
                    </Button>
                  )}
                </Space>
              )
            }
          >
            <Table
              columns={orderItemsColumns}
              dataSource={isEditingOrder ? editedOrderItems : order.order_items}
              rowKey="id"
              pagination={false}
              size="small"
              className="order-items-table order-table-enhanced order-items-table-enhanced"
              style={{ width: '100%', maxWidth: '100%', overflow: 'visible' }}
              scroll={{ x: 1200 }}
              rowClassName={(record) => {
                if (isEditingOrder) {
                  const editedItem = editedOrderItems.find((item) => item.id === record.id);
                  const currentQuantity =
                    editedItem?.quantity !== undefined
                      ? editedItem.quantity
                      : parseFloat(String(record.quantity || 1));
                  return currentQuantity === 0 ? 'deleted-item' : '';
                }
                return '';
              }}
              summary={() => (
                <Table.Summary fixed>
                  {/* 1. الإجمالي */}
                  <Table.Summary.Row className="summary-row">
                    <Table.Summary.Cell
                      index={0}
                      colSpan={isEditingOrder ? 8 : 7}
                      className="summary-label-cell"
                    >
                      <div className="summary-item">
                        <Text strong className="summary-label">
                          {t('orders.orderDetails.subtotal', 'الإجمالي')}
                        </Text>
                        <Text strong className="summary-value">
                          {subtotal.toFixed(2)} {t('common.SAR')}
                        </Text>
                      </div>
                    </Table.Summary.Cell>
                  </Table.Summary.Row>

                  {/* 2. خصم النقاط */}
                  {loyaltyDiscount > 0 && (
                    <Table.Summary.Row className="summary-row">
                      <Table.Summary.Cell
                        index={0}
                        colSpan={isEditingOrder ? 8 : 7}
                        className="summary-label-cell"
                      >
                        <div className="summary-item">
                          <Text strong className="summary-label" style={{ color: '#52c41a' }}>
                            {t('orders.orderDetails.pointsDiscount', 'خصم النقاط')}
                          </Text>
                          <Text strong className="summary-value" style={{ color: '#52c41a' }}>
                            -{loyaltyDiscount.toFixed(2)} {t('common.SAR')}
                          </Text>
                        </div>
                      </Table.Summary.Cell>
                    </Table.Summary.Row>
                  )}

                  {/* 3. الإجمالي بعد الخصم */}
                  {loyaltyDiscount > 0 && subtotalAfterDiscount !== null && (
                    <Table.Summary.Row className="summary-row">
                      <Table.Summary.Cell
                        index={0}
                        colSpan={isEditingOrder ? 8 : 7}
                        className="summary-label-cell"
                      >
                        <div className="summary-item">
                          <Text strong className="summary-label">
                            {t('orders.orderDetails.totalAfterDiscount', 'الإجمالي بعد الخصم')}
                          </Text>
                          <Text strong className="summary-value">
                            {subtotalAfterDiscount.toFixed(2)} {t('common.SAR')}
                          </Text>
                        </div>
                      </Table.Summary.Cell>
                    </Table.Summary.Row>
                  )}

                  {/* 4. قيمة الضريبة */}
                  <Table.Summary.Row className="summary-row">
                    <Table.Summary.Cell
                      index={0}
                      colSpan={isEditingOrder ? 8 : 7}
                      className="summary-label-cell"
                    >
                      <div className="summary-item">
                        <Text strong className="summary-label">
                          {t('orders.orderDetails.taxAmount', 'قيمة الضريبة')}
                        </Text>
                        <Text strong className="summary-value">
                          {taxAmount.toFixed(2)} {t('common.SAR')}
                        </Text>
                      </div>
                    </Table.Summary.Cell>
                  </Table.Summary.Row>

                  {/* 5. الإجمالي شامل الضريبة */}
                  <Table.Summary.Row className="summary-row">
                    <Table.Summary.Cell
                      index={0}
                      colSpan={isEditingOrder ? 8 : 7}
                      className="summary-label-cell"
                    >
                      <div className="summary-item">
                        <Text strong className="summary-label">
                          {t('orders.orderDetails.totalWithTax', 'الإجمالي شامل الضريبة')}
                        </Text>
                        <Text strong className="summary-value">
                          {totalBeforeDiscount.toFixed(2)} {t('common.SAR')}
                        </Text>
                      </div>
                    </Table.Summary.Cell>
                  </Table.Summary.Row>
                </Table.Summary>
              )}
            />
          </Card>

          {/* Order Actions */}
          {(shouldShowAssignDriver ||
            shouldShowAcceptDecline ||
            shouldShowInvoiceReference ||
            shouldShowCancel) && (
            <Card
              title={t('orders.orderDetails.actions')}
              style={{ marginBottom: 24 }}
              className="order-card-enhanced"
            >
              <Space wrap size="middle" className="order-actions-enhanced">
                {/* Assign Driver Button */}
                {shouldShowAssignDriver &&
                  (hasPermission('assign_driver') ||
                    hasPermission('orders.assign_driver') ||
                    hasPermission('assign_order_driver')) && (
                    <Button
                      type="primary"
                      icon={<CarOutlined />}
                      onClick={handleAssignDriverModalOpen}
                      loading={actionLoading}
                    >
                      {t('orders.actions.assign')}
                    </Button>
                  )}

                {/* Accept/Decline Buttons for Draft orders */}
                {shouldShowAcceptDecline && (
                  <>
                    {(hasPermission('approve_order') ||
                      hasPermission('orders.approve') ||
                      hasPermission('update_order_status')) && (
                      <Popconfirm
                        title={t('orders.actions.confirmApprove')}
                        onConfirm={handleApproveOrder}
                        okText={t('common.yes')}
                        cancelText={t('common.no')}
                      >
                        <Button type="primary" icon={<CheckOutlined />} loading={actionLoading}>
                          {t('orders.actions.approve')}
                        </Button>
                      </Popconfirm>
                    )}

                    {(hasPermission('decline_order') ||
                      hasPermission('orders.decline') ||
                      hasPermission('reject_order') ||
                      hasPermission('update_order_status')) && (
                      <Button
                        danger
                        icon={<StopOutlined />}
                        loading={actionLoading}
                        onClick={handleDeclineOrder}
                      >
                        {t('orders.actions.reject')}
                      </Button>
                    )}
                  </>
                )}

                {/* Invoice Reference Button */}
                {shouldShowInvoiceReference && (
                  <Button
                    icon={<EditOutlined />}
                    onClick={handleInvoiceReferenceModalOpen}
                    loading={actionLoading}
                  >
                    {t('orders.actions.editInvoiceRef')}
                  </Button>
                )}

                {/* Cancel Order Button */}
                {shouldShowCancel && (
                  <Popconfirm
                    title={t(
                      'orders.actions.confirmCancel',
                      'Are you sure you want to cancel this order?'
                    )}
                    onConfirm={handleCancelOrder}
                    okText={t('common.yes')}
                    cancelText={t('common.no')}
                  >
                    <Button danger icon={<CloseOutlined />} loading={actionLoading}>
                      {t('orders.actions.cancel', 'Cancel Order')}
                    </Button>
                  </Popconfirm>
                )}
              </Space>
            </Card>
          )}

          {/* Order Status Progression */}
          <Card
            title={t('orders.orderDetails.progressionTitle')}
            style={{ marginBottom: 24 }}
            className="order-card-enhanced"
          >
            {renderStatusProgression()}
          </Card>

          {/* Order History */}
          <Card
            title={t('orders.orderDetails.orderHistory')}
            style={{ marginBottom: 24 }}
            className="order-card-enhanced order-timeline-enhanced"
          >
            {order.order_history && order.order_history.length > 0 ? (
              <Timeline>
                {order.order_history.map((history: OrderHistory) => {
                  const itemChanges = analyzeItemChanges(history);
                  const isUpdatedItems = history.event === 'updated_items';

                  return (
                    <Timeline.Item
                      key={history.id}
                      color={history.event === 'declined_order' ? 'red' : 'blue'}
                    >
                      <div style={{ marginBottom: 8 }}>
                        <div className="timeline-title">{history.event_title}</div>
                        <div className="timeline-time">
                          {new Date(history.created_at).toLocaleString()} -{' '}
                          {history.user?.name ?? '—'}
                        </div>
                        <div className="timeline-description">
                          {translateOrderText(history.description)}
                        </div>

                        {/* Show items comparison for updated_items events */}
                        {isUpdatedItems && itemChanges && (
                          <div style={{ marginTop: 16 }}>
                            <Row gutter={16}>
                              <Col xs={24} md={12}>
                                <Card
                                  size="small"
                                  title={t('orders.orderDetails.originalOrder', 'الطلبية')}
                                  style={{ marginBottom: 16 }}
                                >
                                  <Table
                                    columns={[
                                      {
                                        title: t('orders.orderDetails.product'),
                                        dataIndex: ['product', 'name_ar'],
                                        key: 'product',
                                        render: (name: string, record: any) => (
                                          <div>
                                            <div>{name || record.product?.name_ar}</div>
                                            <Text type="secondary" style={{ fontSize: '11px' }}>
                                              {record.product?.name_en}
                                            </Text>
                                          </div>
                                        ),
                                      },
                                      {
                                        title: t('orders.orderDetails.quantity'),
                                        dataIndex: 'quantity',
                                        key: 'quantity',
                                        width: 80,
                                        render: (qty: string | number) =>
                                          parseFloat(String(qty || 0)).toFixed(2),
                                      },
                                      {
                                        title: t('orders.orderDetails.unitPrice'),
                                        dataIndex: 'unit_price',
                                        key: 'unit_price',
                                        width: 100,
                                        render: (price: string | number) =>
                                          `${parseFloat(String(price || 0)).toFixed(2)} ${t('common.SAR')}`,
                                      },
                                    ]}
                                    dataSource={((history as any).from?.orderItems || []).filter(
                                      (item: unknown) => item != null
                                    )}
                                    rowKey="id"
                                    pagination={false}
                                    size="small"
                                    rowClassName={(record: any) => {
                                      if (
                                        itemChanges.deleted.some(
                                          (item: any) => item.id === record.id
                                        )
                                      ) {
                                        return 'deleted-item';
                                      }
                                      if (
                                        itemChanges.modified.some(
                                          (item: any) => item.from.id === record.id
                                        )
                                      ) {
                                        return 'modified-item';
                                      }
                                      return '';
                                    }}
                                  />
                                </Card>
                              </Col>
                              <Col xs={24} md={12}>
                                <Card
                                  size="small"
                                  title={t(
                                    'orders.orderDetails.orderAfterModification',
                                    'الطلبية بعد التعديل'
                                  )}
                                  style={{ marginBottom: 16 }}
                                >
                                  <Table
                                    columns={[
                                      {
                                        title: t('orders.orderDetails.product'),
                                        dataIndex: ['product', 'name_ar'],
                                        key: 'product',
                                        render: (name: string, record: any) => (
                                          <div>
                                            <div>{name || record.product?.name_ar}</div>
                                            <Text type="secondary" style={{ fontSize: '11px' }}>
                                              {record.product?.name_en}
                                            </Text>
                                          </div>
                                        ),
                                      },
                                      {
                                        title: t('orders.orderDetails.quantity'),
                                        dataIndex: 'quantity',
                                        key: 'quantity',
                                        width: 80,
                                        render: (qty: string | number) =>
                                          parseFloat(String(qty || 0)).toFixed(2),
                                      },
                                      {
                                        title: t('orders.orderDetails.unitPrice'),
                                        dataIndex: 'unit_price',
                                        key: 'unit_price',
                                        width: 100,
                                        render: (price: string | number) =>
                                          `${parseFloat(String(price || 0)).toFixed(2)} ${t('common.SAR')}`,
                                      },
                                    ]}
                                    dataSource={((history as any).to?.orderItems || []).filter(
                                      (item: unknown) => item != null
                                    )}
                                    rowKey="id"
                                    pagination={false}
                                    size="small"
                                    rowClassName={(record: any) => {
                                      if (
                                        itemChanges.added.some((item: any) => item.id === record.id)
                                      ) {
                                        return 'new-item';
                                      }
                                      if (
                                        itemChanges.modified.some(
                                          (item: any) => item.to.id === record.id
                                        )
                                      ) {
                                        return 'modified-item';
                                      }
                                      return '';
                                    }}
                                  />
                                </Card>
                              </Col>
                            </Row>
                          </div>
                        )}
                      </div>
                    </Timeline.Item>
                  );
                })}
              </Timeline>
            ) : (
              <Empty description={t('orders.noHistory')} />
            )}
          </Card>

          {/* Images and Documents */}
          {(order.confirmation_images?.length > 0 || order.delivery_document_url) && (
            <Card
              title={t('orders.orderDetails.documents')}
              style={{ marginBottom: 24 }}
              className="order-card-enhanced"
            >
              <Row gutter={16}>
                {/* Confirmation Images */}
                {order.confirmation_images?.length > 0 && (
                  <Col span={12}>
                    <div style={{ marginBottom: 16 }}>
                      <Text strong>{t('orders.orderDetails.confirmationImages')}</Text>
                    </div>
                    <div className="images-grid">
                      {order.confirmation_images.map((image, index) => (
                        <Image
                          key={index}
                          width={100}
                          height={100}
                          src={`https://driveshield.net/storage/${image}`}
                          style={{ objectFit: 'cover', border: '1px solid #d9d9d9' }}
                          preview={{
                            src: `https://driveshield.net/storage/${image}`,
                          }}
                          loading="lazy"
                          placeholder={
                            <div
                              style={{
                                width: 100,
                                height: 100,
                                background: '#f0f0f0',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                              }}
                            >
                              <Spin size="small" />
                            </div>
                          }
                        />
                      ))}
                    </div>
                  </Col>
                )}

                {/* Delivery Document */}
                {order.delivery_document_url && (
                  <Col span={12}>
                    <div style={{ marginBottom: 16 }}>
                      <Text strong>{t('orders.orderDetails.deliveryDocument')}</Text>
                      <div
                        style={{
                          fontSize: '12px',
                          color: 'var(--color-text-secondary)',
                          marginTop: 4,
                        }}
                      >
                        {order.delivery_document_url.split('/').pop()}
                      </div>
                    </div>
                    <Space direction="vertical" size="small" className="order-actions-enhanced">
                      <Button
                        type="primary"
                        icon={<DownloadOutlined />}
                        href={`https://driveshield.net/storage/${order.delivery_document_url}`}
                        target="_blank"
                        download
                      >
                        {t('orders.downloadDocument')}
                      </Button>
                      <Button
                        size="small"
                        href={`https://driveshield.net/storage/${order.delivery_document_url}`}
                        target="_blank"
                      >
                        {t('orders.viewDocument', 'View Document')}
                      </Button>
                    </Space>
                  </Col>
                )}
              </Row>
            </Card>
          )}

          {/* Decline Reason */}
          {order.decline_reason && (
            <Card
              title={t('orders.orderDetails.declineReason')}
              style={{ marginBottom: 24 }}
              className="order-card-enhanced"
            >
              <Text type="danger">{order.decline_reason}</Text>
            </Card>
          )}

          {/* Notes */}
          {order.notes && (
            <Card
              title={t('orders.orderDetails.notes')}
              style={{ marginBottom: 24 }}
              className="order-card-enhanced"
            >
              <Text>{translateOrderText(order.notes)}</Text>
            </Card>
          )}
        </div>
      ) : (
        // Show message when no order data
        <div style={{ padding: '48px 24px', textAlign: 'center' }}>
          <Empty description={t('orders.messages.noOrderFound')} />
        </div>
      )}

      <Modal
        title={t('orders.actions.assign')}
        open={assignDriverModalVisible}
        onCancel={handleAssignDriverModalClose}
        footer={null}
        width={500}
        className="order-modal-enhanced"
      >
        <Form
          form={assignDriverForm}
          layout="vertical"
          onFinish={handleAssignDriverSubmit}
          className="responsive-form"
        >
          <Form.Item
            label={t('orders.orderDetails.driver')}
            name="driver_id"
            rules={[
              {
                required: true,
                message: t('orders.validation.selectDriver'),
              },
            ]}
          >
            <InfiniteSelect
              queryKey="api/admin/drivers"
              placeholder={t('orders.placeholders.selectDriver')}
              showSearch
              singleMode={true}
              valueField="id"
              searchField="name"
              outerFilters={{
                'per-page': 20, // This will be converted to per-page header
              }}
              labelRender={(option: any) => {
                const driver = option?.item || option;
                if (!driver) {
                  return t('common.noData');
                }
                return `${driver.name || driver.title || t('common.noData')}${driver.phone ? ` (${driver.phone})` : driver.phone_number ? ` (${driver.phone_number})` : ''}`;
              }}
            />
          </Form.Item>

          <Form.Item>
            <Space className="order-actions-enhanced">
              <Button type="primary" htmlType="submit" loading={actionLoading}>
                {t('orders.actions.assign')}
              </Button>
              <Button onClick={handleAssignDriverModalClose}>{t('common.cancel')}</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Invoice Reference Modal */}
      <Modal
        title={t('orders.actions.editInvoiceRef')}
        open={invoiceReferenceModalVisible}
        onCancel={handleInvoiceReferenceModalClose}
        footer={null}
        width={500}
        className="order-modal-enhanced"
      >
        <Form
          form={invoiceForm}
          layout="vertical"
          onFinish={handleInvoiceReferenceSubmit}
          className="responsive-form"
        >
          <Form.Item
            label={t('orders.orderDetails.invoiceRef')}
            name="invoice_reference"
            rules={[
              {
                required: true,
                message: t('orders.validation.invoiceRefRequired'),
              },
            ]}
          >
            <Input placeholder={t('orders.placeholders.invoiceRef')} />
          </Form.Item>

          <Form.Item>
            <Space className="order-actions-enhanced">
              <Button type="primary" htmlType="submit" loading={actionLoading}>
                {t('common.update')}
              </Button>
              <Button onClick={handleInvoiceReferenceModalClose}>{t('common.cancel')}</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Decline Reason Modal */}
      <Modal
        title={t('orders.actions.declineReason')}
        open={declineReasonModalVisible}
        onCancel={handleDeclineReasonModalClose}
        footer={null}
        width={500}
        className="order-modal-enhanced"
      >
        <Form
          form={declineReasonForm}
          layout="vertical"
          onFinish={handleDeclineReasonSubmit}
          className="responsive-form"
        >
          <Form.Item
            label={t('orders.orderDetails.declineReason')}
            name="decline_reason"
            rules={[
              {
                message: t('orders.validation.declineReasonRequired'),
              },
              {
                min: 5,
                message: t('orders.validation.declineReasonMinLength'),
              },
            ]}
          >
            <Input.TextArea
              rows={4}
              placeholder={t('orders.placeholders.declineReason')}
              maxLength={500}
              showCount
            />
          </Form.Item>

          <Form.Item>
            <Space className="order-actions-enhanced">
              <Button
                type="primary"
                danger
                htmlType="submit"
                loading={actionLoading}
                icon={<StopOutlined />}
              >
                {t('orders.actions.confirmDecline')}
              </Button>
              <Button onClick={handleDeclineReasonModalClose}>{t('common.cancel')}</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Customer Classification Modal */}
      <CustomerClassificationModal
        visible={classificationModalVisible}
        customer={selectedCustomer}
        onClose={() => {
          setClassificationModalVisible(false);
          setSelectedCustomer(null);
        }}
        onSuccess={handleClassificationSuccess}
      />
    </Modal>
  );
};

export default React.memo(OrderDetailsModal);
